import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Button, Checkbox, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AccessTime as ClockIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { dateFormat, timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { getSessionLineColor, getAbsenteeLabel } from 'common/utils/sessionColor';
import SessionStatusChip from 'components/session/SessionStatusChip';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Card de una sesión en la lista (reemplaza las filas de tabla con scroll). Línea de color
 * a la izquierda por proximidad/estado; superficie premium mode-aware. Click abre el detalle.
 */
function SessionListItem({
  session,
  displayStatus = null,
  dateLocale,
  selected = false,
  actionLabel = null,
  onAction = null,
  onDelete = null,
  actionNode = null,
  person = null,
  personLabel = null,
  selectable = false,
  checked = false,
  onToggleSelect = null
}) {
  const theme = useTheme();
  const { t } = useTranslation(['sessions']);

  const status = displayStatus ?? session.status;
  const lineColor = getSessionLineColor(theme, status, session.start_date);
  // Quién faltó (Cliente/Entrenador) si la sesión es una ausencia atribuida — útil en ambas vistas.
  const absentee = getAbsenteeLabel(t, status);
  const dayName = capitalize(convertUtcToLocalTime(session.start_date, 'EEEE', dateLocale));
  const dateLabel = convertUtcToLocalTime(session.start_date, dateFormat, dateLocale);
  const startTime = convertUtcToLocalTime(session.start_date, timeFormat);
  const endTime = convertUtcToLocalTime(session.end_date, timeFormat);
  // `person` permite mostrar al cliente (vista entrenador) en vez del entrenador (por defecto).
  const trainer = person || session.trainer || session.trainer_id;
  const category = session.session_type_id?.name;

  return (
    <Box
      onClick={onAction}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAction?.(); } }}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        backgroundColor: 'surface.raised',
        border: '1px solid',
        borderColor: selected ? 'secondary.main' : 'hairline.main',
        borderLeft: `4px solid ${lineColor}`,
        borderRadius: '14px',
        p: { xs: 1.5, sm: 2 },
        transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s, border-color 0.2s',
        boxShadow: theme.palette.shadow.card,
        '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' }
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.25, sm: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        {selectable && (
          <Checkbox
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
            size="small"
            inputProps={{ 'aria-label': 'seleccionar sesión' }}
            sx={{ p: 0.5, color: 'text.secondary', '&.Mui-checked': { color: 'secondary.main' } }}
          />
        )}
        {/* Fecha + hora */}
        <Box sx={{ minWidth: { sm: 140 } }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{dayName}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dateLabel}</Typography>
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: { sm: 150 } }}>
          <ClockIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{startTime} – {endTime}</Typography>
        </Stack>

        {/* Persona: entrenador (por defecto) o cliente (vista entrenador, con etiqueta). Con
            `personLabel` se muestra también en mobile, encima de la fila de estado + "Marcar como". */}
        {trainer && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexGrow: 1, display: personLabel ? 'flex' : { xs: 'none', md: 'flex' } }}>
            <Avatar src={trainer.photo} alt={trainer.full_name} sx={{ width: 26, height: 26, fontSize: '0.72rem' }}>
              {trainer.full_name?.[0]}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{trainer.full_name}</Typography>
              {personLabel && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
                  {personLabel}
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {category && (
          <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', lg: 'block' }, minWidth: 90 }} noWrap>
            {category}
          </Typography>
        )}

        {/* Estado + acción */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
          sx={{ flexShrink: 0 }}
        >
          <Stack direction="column" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.25}>
            <SessionStatusChip status={status} startDate={session.start_date} />
            {absentee && (
              <Typography variant="caption" sx={{ color: (th) => th.palette.sessionStatus.absent.fg, fontWeight: 700, lineHeight: 1 }}>
                {absentee}
              </Typography>
            )}
          </Stack>
          {onDelete && (
            <IconButton
              size="small"
              aria-label="delete"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              sx={{ color: (th) => th.palette.sessionStatus.absent.fg }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
          {actionNode && (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <Box onClick={(e) => e.stopPropagation()}>{actionNode}</Box>
          )}
          {!actionNode && actionLabel && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={(e) => { e.stopPropagation(); onAction?.(); }}
              sx={{ color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {actionLabel}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

SessionListItem.propTypes = {
  session: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  dateLocale: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  onDelete: PropTypes.func,
  actionNode: PropTypes.node,
  person: PropTypes.object,
  personLabel: PropTypes.string,
  selectable: PropTypes.bool,
  checked: PropTypes.bool,
  onToggleSelect: PropTypes.func
};

export default SessionListItem;
